import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  SizeSelection,
  SizeSelection_Size,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {COLORS, SizeAdvice, useInView} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {InlineSizeSelection} from './InlineSizeSelection';
import {SizeGroups} from './SizeGroups';
import {SizeRecommendationInfo} from './SizeRecommendationInfo';
import {SizeRuns} from './SizeRuns';
import {Sizes} from './Sizes';
import {CommonSizeProps} from './types/CommonSizeProps';

interface SizeSectionProps {
  onSizeSelect: (size: SizeSelection_Size) => void;
  product: Product;
  selectedSize: SizeSelection_Size | undefined;
  sizeSelection: SizeSelection;
  onSizeAdvisorSizeSelection?: (sizeId: number) => void;
  enableRebrush?: boolean;
  showSizeReminder: (size: SizeSelection_Size) => void;
  disableSoldOut?: boolean;
  setContentFullHeight?: boolean;
}

export const SizeSection: React.FC<SizeSectionProps> = ({
  product,
  onSizeSelect,
  selectedSize,
  sizeSelection,
  enableRebrush,
  showSizeReminder,
  disableSoldOut,
  setContentFullHeight,
}) => {
  const type = sizeSelection?.type;

  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const handleSizeSelectFlyout = (size: SizeSelection_Size) => {
    onSizeSelect(size);
  };

  const handleShowSizeReminder = (size: SizeSelection_Size) => {
    showSizeReminder(size);
  };

  const {ref, inView} = useInView<HTMLDivElement>({
    triggerOnce: true,
    threshold: 0.1,
  });

  if (!type) {
    return null;
  }

  const commonSizeProps: CommonSizeProps = {
    onShowSizeReminder: handleShowSizeReminder,
    onSizeSelect: handleSizeSelectFlyout,
    product,
    selectedSize,
    inView,
    shouldUseRemindMeBellIconButton: enableRebrush,
    enableHighlightSelectedSizeTab: enableRebrush,
    enableYourSizeGreyBadges: enableRebrush,
    setContentFullHeight,
    enablePriceOverhaulV2: isPriceDisplayOverhaulV2Active,
  };

  return (
    <Container ref={ref}>
      {type.$case === 'inlineSelection' && (
        <InlineSizeSelection
          onShowSizeReminder={showSizeReminder}
          onSizeSelect={onSizeSelect}
          selectedSize={selectedSize}
          sizes={type.inlineSelection.sizes}
          enableYourSizeGreyBadges={enableRebrush}
          shouldUseRemindMeBellIconButton={enableRebrush}
        />
      )}

      {type.$case === 'sizes' && (
        <>
          {type.sizes.sizeAdvice && (
            <SizeAdviceNote
              label={type.sizes.sizeAdvice}
              data-grey-background={enableRebrush}
              $removeBorderTop={setContentFullHeight}
            />
          )}
          <Sizes
            {...commonSizeProps}
            sizes={type.sizes.sizes}
            disableSoldOut={disableSoldOut}
          />
        </>
      )}

      {type.$case === 'oneSize' && (
        <Sizes
          {...commonSizeProps}
          sizes={[type.oneSize.size]}
          disableSoldOut={disableSoldOut}
        />
      )}

      {type.$case === 'sizeRuns' && (
        <>
          {type.sizeRuns.sizeAdvice && (
            <SizeAdviceNote
              label={type.sizeRuns.sizeAdvice}
              data-grey-background={enableRebrush}
              $removeBorderTop={setContentFullHeight}
            />
          )}
          <SizeRuns
            {...commonSizeProps}
            removeBorderTop={setContentFullHeight && !type.sizeRuns.sizeAdvice}
            sizeRuns={type.sizeRuns.sizeRuns}
            disableSoldOut={disableSoldOut}
          />
        </>
      )}

      {type.$case === 'groupedSizes' && (
        <SizeGroups
          {...commonSizeProps}
          sizeGroups={type.groupedSizes.groups}
          enableSizeAdviceWithGreyBackground={enableRebrush}
        />
      )}
      <SizeRecommendationInfo />
    </Container>
  );
};

const SizeAdviceNote = styled(SizeAdvice)<{
  newSizeAdviceThing?: boolean;
  $removeBorderTop?: boolean;
  $addBorderBottom?: boolean;
}>`
  border-bottom: 1px solid ${COLORS.Grey4};
  border-top: ${props =>
    props.$removeBorderTop ? 'unset' : `1px solid ${COLORS.Grey3}`};

  &[data-grey-background='true'] {
    border-top: 1px solid ${COLORS.Grey4};
    background-color: ${COLORS.Grey5};
    padding: 10px 18px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;
